import { get, set } from 'idb-keyval';

export type OfflineOperationType = 
  | 'CREATE_ORDER' 
  | 'CREATE_TAKEAWAY_ORDER' 
  | 'UPDATE_ORDER_STATUS'
  | 'ADD_ITEMS'
  | 'CREATE_PAYMENT';

export interface QueuedOperation {
  id: string; // uuid
  type: OfflineOperationType;
  payload: any;
  timestamp: string;
  retries: number;
  token?: string;
}

const QUEUE_KEY = 'DufiWaiter_OfflineQueue';

export class OfflineQueueService {
  static async getQueue(): Promise<QueuedOperation[]> {
    try {
      const queue = await get<QueuedOperation[]>(QUEUE_KEY);
      return queue || [];
    } catch {
      return [];
    }
  }

  static async saveQueue(queue: QueuedOperation[]): Promise<void> {
    try {
      await set(QUEUE_KEY, queue);
    } catch (err) {
      console.error('Failed to save offline queue', err);
    }
  }

  static async pushOperation(type: OfflineOperationType, payload: any): Promise<void> {
    const queue = await this.getQueue();
    const token = localStorage.getItem('authToken') || undefined;
    const newOp: QueuedOperation = {
      id: crypto.randomUUID(),
      type,
      payload,
      timestamp: new Date().toISOString(),
      retries: 0,
      token
    };
    queue.push(newOp);
    await this.saveQueue(queue);

    // Intentar disparar Background Sync si el navegador lo soporta
    if ('serviceWorker' in navigator && 'SyncManager' in window) {
      const swRegistration = await navigator.serviceWorker.ready;
      try {
        await (swRegistration as any).sync.register('sync-offline-operations');
      } catch (err) {}
    }
  }

  static async removeOperation(id: string): Promise<void> {
    const queue = await this.getQueue();
    const updated = queue.filter(op => op.id !== id);
    await this.saveQueue(updated);
  }

  static async incrementRetry(id: string): Promise<void> {
    const queue = await this.getQueue();
    const idx = queue.findIndex(op => op.id === id);
    if (idx !== -1) {
      queue[idx].retries += 1;
      await this.saveQueue(queue);
    }
  }

  static async clearQueue(): Promise<void> {
    await this.saveQueue([]);
  }
}
